import { httpClient } from "./http-client";

export interface TranslationService {
  translateText(
    text: string,
    targetLanguage: string,
    sourceLanguage?: string,
  ): Promise<string>;
  translateBatch(
    texts: string[],
    targetLanguage: string,
    sourceLanguage?: string,
  ): Promise<string[]>;
  detectLanguage(text: string): Promise<string>;
}

export const SUPPORTED_LANGUAGES = {
  ru: "ru",
  en: "en",
  de: "de",
  fr: "fr",
  es: "es",
} as const;

export type SupportedLanguage = keyof typeof SUPPORTED_LANGUAGES;

interface BackendTranslateResponse {
  translatedText: string;
  sourceLang: string;
  targetLang: string;
  fromCache: boolean;
}

class BackendTranslationService implements TranslationService {
  async translateText(
    text: string,
    targetLanguage: string,
    sourceLanguage?: string,
  ): Promise<string> {
    if (!text || !text.trim()) return text;

    try {
      const response = await httpClient.post<BackendTranslateResponse>(
        "/translation/translate",
        {
          text,
          targetLang: targetLanguage,
          ...(sourceLanguage && { sourceLang: sourceLanguage }),
        },
      );
      return response.data.translatedText;
    } catch (error) {
      console.error("Translation error:", error);
      return text;
    }
  }

  async translateBatch(
    texts: string[],
    targetLanguage: string,
    sourceLanguage?: string,
  ): Promise<string[]> {
    if (!texts.length) return texts;

    try {
      const response = await httpClient.post<BackendTranslateResponse[]>(
        "/translation/translate/batch",
        {
          texts,
          targetLang: targetLanguage,
          ...(sourceLanguage && { sourceLang: sourceLanguage }),
        },
      );
      return response.data.map((r) => r.translatedText);
    } catch (error) {
      console.error("Batch translation error:", error);
      return texts;
    }
  }

  async detectLanguage(text: string): Promise<string> {
    if (!text || !text.trim()) return "en";

    try {
      const response = await httpClient.post<BackendTranslateResponse>(
        "/translation/translate",
        { text, targetLang: "en" },
      );
      return response.data.sourceLang || "en";
    } catch {
      return "en";
    }
  }

  async isAvailable(): Promise<boolean> {
    return true;
  }

  async getSupportedLanguages(): Promise<
    Array<{ code: string; name: string }>
  > {
    return [
      { code: "ru", name: "Russian" },
      { code: "en", name: "English" },
      { code: "de", name: "German" },
      { code: "fr", name: "French" },
      { code: "es", name: "Spanish" },
    ];
  }
}

export const translationService = new BackendTranslationService();

export const useTranslationService = () => ({
  translateText: translationService.translateText.bind(translationService),
  translateBatch: translationService.translateBatch.bind(translationService),
  detectLanguage: translationService.detectLanguage.bind(translationService),
  isAvailable: translationService.isAvailable.bind(translationService),
  getSupportedLanguages:
    translationService.getSupportedLanguages.bind(translationService),
});

export const translationUtils = {
  async translateAIContent(
    content: string,
    targetLanguage: string,
    context?: string,
  ): Promise<string> {
    if (targetLanguage === "en") return content;
    const textToTranslate = context
      ? `[Context: ${context}] ${content}`
      : content;
    return translationService.translateText(
      textToTranslate,
      targetLanguage,
      "en",
    );
  },

  async translateAIMessages(
    messages: Array<{ content: string; context?: string }>,
    targetLanguage: string,
  ): Promise<string[]> {
    if (targetLanguage === "en") return messages.map((m) => m.content);
    const texts = messages.map((m) =>
      m.context ? `[Context: ${m.context}] ${m.content}` : m.content,
    );
    return translationService.translateBatch(texts, targetLanguage, "en");
  },

  cache: new Map<string, string>(),

  async getCachedTranslation(
    text: string,
    targetLanguage: string,
    context?: string,
  ): Promise<string> {
    const cacheKey = `${text}:${targetLanguage}:${context || ""}`;
    if (this.cache.has(cacheKey)) return this.cache.get(cacheKey)!;
    const translation = await this.translateAIContent(
      text,
      targetLanguage,
      context,
    );
    this.cache.set(cacheKey, translation);
    return translation;
  },

  clearCache(): void {
    this.cache.clear();
  },
};

export default translationService;
